import { Component, computed, input, signal } from '@angular/core';
import { GoogleMapsScaleControlInput } from '../../model/google-maps-input.interface';

@Component({
  selector: 'pozi-scale-control',
  imports: [],
  templateUrl: './scale-control.component.html',
  styleUrl: './scale-control.component.scss',
})
export class ScaleControlComponent {
  private readonly tileSize = 256;
  private readonly earthCircumference = 40075017;
  private readonly googleMapsMinScaleWidth = 50;
  private readonly googleMapsMaxScaleWidth = 80;
  private googleMapsDistanceValues = signal<number[]>(
    this.getGoogleMapsDistanceValues()
  );

  input = input<GoogleMapsScaleControlInput | null>();
  icon = input<string>('google.svg');

  scale = computed<number | undefined>(() => {
    const zoom = this.input()?.zoom;
    const lat = this.input()?.lat;

    // Nullish, not falsy: lat 0 (the equator) and zoom 0 are both valid.
    if (zoom == null || lat == null) return;

    return (
      (this.earthCircumference * Math.cos((lat * Math.PI) / 180)) /
      (Math.pow(2, zoom) * this.tileSize)
    );
  });

  googleMapsDistance = computed<number | undefined>(() => {
    const scale = this.scale();
    if (!scale) return;

    const minScale = Math.floor(scale * this.googleMapsMinScaleWidth);
    const maxScale = Math.ceil(scale * this.googleMapsMaxScaleWidth);

    return this.googleMapsDistanceValues().find((value, index, values) => {
      const nextValue = values[index + 1];
      return (
        (minScale <= value && value <= maxScale) ||
        (minScale > value && nextValue && maxScale < nextValue)
      );
    });
  });

  formattedDistance = computed<string | undefined>(() => {
    const dist = this.googleMapsDistance();
    if (!dist) return;
    return dist >= 1000 ? `${dist / 1000} km` : `${dist} m`;
  });

  barWidth = computed<number | undefined>(() => {
    const dist = this.googleMapsDistance();
    const scale = this.scale();

    if (!dist || !scale) return;
    return dist / scale;
  });

  private getGoogleMapsDistanceValues(): number[] {
    const result = [];
    for (let i = 0; i <= 6; i++) {
      const factor = Math.pow(10, i);
      result.push(1 * factor, 2 * factor, 5 * factor);
    }
    return result;
  }
}
